import { c as _c } from "react/compiler-runtime";
import Head from "next/head";
import { absoluteUrl, SITE } from "@/lib/seo";
import { truncateMeta } from "@/lib/formatCopy";

/**
 * Site-wide SEO: title, description, canonical, Open Graph, Twitter, robots.
 */
export default function SeoHead(t0) {
  const $ = _c(59);
  const {
    title,
    description,
    path: t1,
    image: t2,
    type: t3,
    noindex: t4,
    jsonLd: t5
  } = t0;
  const path = t1 === undefined ? "/" : t1;
  const image = t2 === undefined ? SITE.ogImage : t2;
  const type = t3 === undefined ? "website" : t3;
  const noindex = t4 === undefined ? false : t4;
  const jsonLd = t5 === undefined ? null : t5;
  const pageTitle = title || SITE.name;
  const t6 = description || SITE.description;
  let t7;
  if ($[0] !== t6) {
    t7 = truncateMeta(t6);
    $[0] = t6;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const pageDescription = t7;
  let t8;
  if ($[2] !== path) {
    t8 = absoluteUrl(path);
    $[2] = path;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const canonical = t8;
  let t9;
  if ($[4] !== image) {
    t9 = image?.startsWith("http") ? image : absoluteUrl(image);
    $[4] = image;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const ogImage = t9;
  const robots = noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large";
  let t10;
  if ($[6] !== jsonLd) {
    t10 = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    $[6] = jsonLd;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  const jsonLdBlocks = t10;
  let t11;
  if ($[8] !== pageTitle) {
    t11 = <title>{pageTitle}</title>;
    $[8] = pageTitle;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] !== pageDescription) {
    t12 = <meta name="description" content={pageDescription} />;
    $[10] = pageDescription;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  let t14;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <meta name="keywords" content={SITE.keywords.join(", ")} />;
    t14 = <meta name="author" content={SITE.legalName} />;
    $[12] = t13;
    $[13] = t14;
  } else {
    t13 = $[12];
    t14 = $[13];
  }
  let t15;
  if ($[14] !== robots) {
    t15 = <meta name="robots" content={robots} />;
    $[14] = robots;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] !== canonical) {
    t16 = <link rel="canonical" href={canonical} />;
    $[16] = canonical;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] !== type) {
    t17 = <meta property="og:type" content={type} />;
    $[18] = type;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <meta property="og:site_name" content={SITE.name} />;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] !== pageTitle) {
    t19 = <meta property="og:title" content={pageTitle} />;
    $[21] = pageTitle;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] !== pageDescription) {
    t20 = <meta property="og:description" content={pageDescription} />;
    $[23] = pageDescription;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== canonical) {
    t21 = <meta property="og:url" content={canonical} />;
    $[25] = canonical;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  let t22;
  if ($[27] !== ogImage) {
    t22 = <meta property="og:image" content={ogImage} />;
    $[27] = ogImage;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] !== pageTitle) {
    t23 = <meta property="og:image:alt" content={pageTitle} />;
    $[29] = pageTitle;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  let t25;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <meta property="og:locale" content={SITE.locale} />;
    t25 = <meta name="twitter:card" content="summary_large_image" />;
    $[31] = t24;
    $[32] = t25;
  } else {
    t24 = $[31];
    t25 = $[32];
  }
  let t26;
  if ($[33] !== pageTitle) {
    t26 = <meta name="twitter:title" content={pageTitle} />;
    $[33] = pageTitle;
    $[34] = t26;
  } else {
    t26 = $[34];
  }
  let t27;
  if ($[35] !== pageDescription) {
    t27 = <meta name="twitter:description" content={pageDescription} />;
    $[35] = pageDescription;
    $[36] = t27;
  } else {
    t27 = $[36];
  }
  let t28;
  if ($[37] !== ogImage) {
    t28 = <meta name="twitter:image" content={ogImage} />;
    $[37] = ogImage;
    $[38] = t28;
  } else {
    t28 = $[38];
  }
  let t29;
  let t30;
  let t31;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = SITE.twitterHandle ? <meta name="twitter:site" content={SITE.twitterHandle} /> : null;
    t30 = <meta name="theme-color" content={SITE.themeColor} />;
    t31 = <meta name="application-name" content={SITE.shortName} />;
    $[39] = t29;
    $[40] = t30;
    $[41] = t31;
  } else {
    t29 = $[39];
    t30 = $[40];
    t31 = $[41];
  }
  let t32;
  if ($[42] !== jsonLdBlocks) {
    t32 = jsonLdBlocks.map(_temp);
    $[42] = jsonLdBlocks;
    $[43] = t32;
  } else {
    t32 = $[43];
  }
  let t33;
  if ($[44] !== t11 || $[45] !== t12 || $[46] !== t15 || $[47] !== t16 || $[48] !== t17 || $[49] !== t19 || $[50] !== t20 || $[51] !== t21 || $[52] !== t22 || $[53] !== t23 || $[54] !== t26 || $[55] !== t27 || $[56] !== t28 || $[57] !== t32) {
    t33 = <Head>{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t24}{t25}{t26}{t27}{t28}{t29}{t30}{t31}{t32}</Head>;
    $[44] = t11;
    $[45] = t12;
    $[46] = t15;
    $[47] = t16;
    $[48] = t17;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
    $[54] = t26;
    $[55] = t27;
    $[56] = t28;
    $[57] = t32;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  return t33;
}
function _temp(block, i) {
  return <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{
    __html: JSON.stringify(block)
  }} />;
}